/**
 * lib/auth/telephone.ts — un numéro saisi devient un numéro envoyable.
 *
 * Personne ne tape « +33612345678 ». On tape « 06 12 34 56 78 », avec des
 * points, des tirets ou rien. Brevo, lui, n'accepte que l'E.164. Entre les
 * deux, il faut trancher, et trancher au bord : un numéro mal formé qui entre
 * en base devient un compte auquel plus aucun code ne peut être envoyé — donc
 * un compte définitivement fermé à son propriétaire.
 *
 * PAS DE BIBLIOTHÈQUE. `libphonenumber-js` pèse 150 ko pour couvrir 200 pays ;
 * OGRYN vend en France et, au pire, dans le Maghreb. Un indicatif explicite
 * suffit, et ce qu'on ne sait pas lire, on le refuse plutôt que de le deviner.
 */

/** Le pays par défaut quand le numéro commence par un 0 national. */
const INDICATIF_DEFAUT = '33';

/**
 * Rend le numéro en E.164 (`+33612345678`), ou `null` s'il n'est pas lisible.
 * Le refus est volontairement franc : mieux vaut faire ressaisir que d'envoyer
 * un code dans le vide.
 */
export function normaliserTelephone(saisie: string): string | null {
  if (!saisie) return null;

  // Espaces, points, tirets, parenthèses et espaces insécables : du décor.
  let n = saisie.replace(/[\s.\-()  ]/g, '');
  if (!n) return null;

  // `0033…` et `+33…` disent la même chose.
  if (n.startsWith('00')) n = `+${n.slice(2)}`;

  if (n.startsWith('+')) {
    if (!/^\+[1-9]\d{7,14}$/.test(n)) return null;
    return n;
  }

  // Sans indicatif, on n'accepte QUE la forme nationale française : dix
  // chiffres commençant par 0. Un « 612345678 » nu pourrait être un numéro
  // français amputé comme un numéro étranger sans son indicatif — et se
  // tromper enverrait le code à un inconnu.
  if (!/^0\d{9}$/.test(n)) return null;
  return `+${INDICATIF_DEFAUT}${n.slice(1)}`;
}

/** Ce qu'on affiche en clair : assez pour se reconnaître, pas pour recopier. */
export function masquerTelephone(numero: string): string {
  return `${'•'.repeat(Math.max(numero.length - 4, 0))}${numero.slice(-4)}`;
}

/**
 * Le domaine des adresses de substitution fabriquées à l'inscription par
 * téléphone. `.invalid` est réservé par la RFC 2606 : aucun message ne peut
 * partir vers lui, même par erreur de configuration.
 */
export const DOMAINE_SUBSTITUTION = '@telephone.invalid';

export const estAdresseDeSubstitution = (email: string | null | undefined): boolean =>
  !!email && email.endsWith(DOMAINE_SUBSTITUTION);

/**
 * Ce qu'on inscrit comme moyens de contact sur le compte MÉTIER.
 *
 * Better Auth exige une adresse : un compte créé par téléphone en reçoit donc
 * une, fabriquée. Elle a sa place dans la table d'identité — c'est une clé —
 * et aucune dans `users.emails`, qui sert à ÉCRIRE aux gens. Recopiée là, elle
 * ferait apparaître « 33612345678@telephone.invalid » sur les factures et dans
 * les relances, et chaque envoi partirait dans le vide.
 */
export function contactsInscription(email: string | null | undefined, telephone: string | null | undefined): {
  emails: { email: string; primary: true }[];
  phones: { number: string; primary: true }[];
} {
  return {
    emails: email && !estAdresseDeSubstitution(email) ? [{ email, primary: true }] : [],
    phones: telephone ? [{ number: telephone, primary: true }] : [],
  };
}

/**
 * Les canaux sur lesquels on accepte d'envoyer un code de signature.
 *
 * L'exigence b) du procédé veut que le code parte sur un identifiant rattaché
 * au signataire — donc PROUVÉ. Les coordonnées du profil prestataire
 * (`public_phone`, `public_email`) sont saisies librement : elles n'attestent
 * de rien et n'ont rien à faire ici. Seul le compte d'identité fait foi.
 */
export function canauxVerifies(compte: {
  email?: string | null;
  emailVerified?: boolean | null;
  phoneNumber?: string | null;
  phoneNumberVerified?: boolean | null;
} | null | undefined): { email: string | null; phone: string | null } {
  if (!compte) return { email: null, phone: null };
  return {
    // Une adresse de substitution est vérifiée par construction et n'atteint
    // personne : la proposer comme canal enverrait le code dans le vide.
    email: compte.emailVerified && compte.email && !estAdresseDeSubstitution(compte.email)
      ? compte.email : null,
    phone: compte.phoneNumberVerified && compte.phoneNumber ? compte.phoneNumber : null,
  };
}

/** Le témoin qui porte le rôle quand le formulaire ne peut pas le transmettre. */
export const COOKIE_ROLE = 'ogryn_role';

/**
 * Le rôle choisi sur /bienvenue, d'où qu'il vienne.
 *
 * L'inscription par e-mail le passe en champ. L'inscription par TÉLÉPHONE ne
 * le peut pas : Better Auth crée le compte lui-même à la vérification du code
 * (`signUpOnVerification`), sans rien connaître du formulaire. Le rôle voyage
 * donc dans un témoin, comme il voyage dans l'adresse de retour pour Google et
 * Apple — et pour la même raison : sans lui, un futur prestataire atterrit dans
 * l'espace particulier (cas vécu le 27/07).
 *
 * Une valeur inconnue vaut `null` : elle ne donne aucun droit — les profils
 * font foi — mais la croire ferait créer le mauvais profil.
 */
export function roleInscription(
  champ: string | null | undefined,
  temoin: string | null | undefined,
): 'client' | 'prestataire' | null {
  const v = champ ?? temoin ?? null;
  return v === 'client' || v === 'prestataire' ? v : null;
}
